import React, { useState, useEffect } from 'react';
import { validateField } from '../../utils/validationRules';
import { AlertTriangle, XCircle, CheckCircle2 } from 'lucide-react';

const ValidatedInput = ({ 
  label, 
  name, 
  value, 
  onChange, 
  lang = 'en', 
  type = 'number',
  step = '1',
  placeholder,
  className = '',
  required = false
}) => {
  const [status, setStatus] = useState('valid');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (touched || value !== '') {
      const result = validateField(name, value, lang);
      setStatus(result.status);
      setMessage(result.message);
    } else {
      setStatus('valid');
      setMessage('');
    }
  }, [value, name, lang, touched]);

  const handleBlur = () => {
    setTouched(true);
  };

  const isRTL = lang === 'ar';

  let borderClass = 'border-gray-200 dark:border-slate-600 focus:border-[#C47A2C]';
  let icon = null;
  let textClass = 'text-gray-500';

  if (status === 'error') {
    borderClass = 'border-rose-500 focus:border-rose-500';
    icon = <XCircle size={16} className="text-rose-500" />;
    textClass = 'text-rose-500';
  } else if (status === 'warning') {
    borderClass = 'border-amber-500 focus:border-amber-500';
    icon = <AlertTriangle size={16} className="text-amber-500" />;
    textClass = 'text-amber-500';
  } else if (status === 'valid' && touched && value !== '') {
    borderClass = 'border-emerald-500 focus:border-emerald-500';
    icon = <CheckCircle2 size={16} className="text-emerald-500" />;
  }

  return (
    <div className={`flex flex-col relative ${className}`}>
      {label && (
        <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1 ml-1 flex justify-between">
          <span>{label} {required && <span className="text-rose-500">*</span>}</span>
        </label>
      )}
      <div className="relative flex items-center">
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
          step={step}
          placeholder={placeholder}
          className={`bg-gray-50 dark:bg-slate-900 border ${borderClass} rounded-xl p-2.5 text-[#1B263B] dark:text-white font-bold focus:bg-white dark:focus:bg-slate-800 outline-none transition-all tabular-nums shadow-inner w-full ${isRTL ? 'pl-10' : 'pr-10'}`}
          onKeyDown={(e) => {
            // Reject alphabetic characters in number inputs
            if (type === 'number' && ['e', 'E', '+', '-'].includes(e.key)) {
              e.preventDefault();
            }
          }}
        />
        {icon && (
          <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}>
            {icon}
          </div>
        )}
      </div>
      {message && (
        <span className={`text-[10px] font-bold mt-1 ml-1 ${textClass} animate-fade-in`}>
          {message}
        </span>
      )}
    </div>
  );
};

export default ValidatedInput;
